import StopOutlined from '@mui/icons-material/StopOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount } from '../../components/Amount';
import { ConfirmSheet } from '../../components/ConfirmSheet';
import { reasonMessage } from '../../i18n';
import type { ApiError } from '../../lib/api/api-client';

import { PositionLabel } from './PositionLabel';
import { type CloseOption, type FxmatePosition, hasCurrentResult } from './emergency-model';
import { ROW_SEPARATOR_SX } from './row-style';

/** Props of {@link EmergencyStopSheet}. */
export interface EmergencyStopSheetProps {
  /**
   * Opened by "Retry closing" (Q104): confirm is enabled only with the option checked (no
   * pause-only stop from here), and an empty list says there is nothing left to close.
   */
  readonly retryClose?: boolean | undefined;
  /**
   * Channels the stop will pause, from `GET /me` (those not already paused, M4 R2); `null` while
   * unknown, since the stop never waits for it (FXM-02 §7).
   */
  readonly channelCount: number | null;
  /** What the close option offers, from `GET /positions` (M4 R6, R7). */
  readonly closeOption: CloseOption;
  /** A stop is in flight: the sheet is locked and the confirm button shows progress. */
  readonly stopping: boolean;
  /** Failure of the last stop: its reason text shows above the buttons, which retry it. */
  readonly error: ApiError | null;
  /** Confirm tapped, with the trader's explicit choice (never pre-checked, I7). */
  readonly onConfirm: (closePositions: boolean) => void;
  /** Cancel, Escape or the backdrop, when no stop is in flight. */
  readonly onCancel: () => void;
}

/**
 * E10's confirmation (mockup 8), built on C3's `ConfirmSheet`: how many channels pause, the close
 * option (never pre-checked: `ConfirmSheet` resets it on every opening and has no prop to set it,
 * I7, M4 R3) with each FXMate position and its current result (R4), and the manual-positions note
 * (R5). The confirm label says when positions will be closed too (R4). "Retry closing" opens it
 * again, with the positions read anew (Q104): it then confirms only with the option checked.
 */
export function EmergencyStopSheet({
  retryClose = false,
  channelCount,
  closeOption,
  stopping,
  error,
  onConfirm,
  onCancel,
}: EmergencyStopSheetProps): ReactElement {
  const { t } = useTranslation();
  const count = closeOption.kind === 'available' ? closeOption.positions.length : 0;
  const failure = error === null ? null : reasonMessage(error.reasonCode, error.params);
  return (
    <ConfirmSheet
      open
      title={t('emergency.title')}
      icon={<StopOutlined />}
      confirmLabel={(checked) =>
        stopping
          ? t('emergency.stopping')
          : checked
            ? t('emergency.confirm.close', { count })
            : retryClose
              ? t('emergency.result.retryClose')
              : t('emergency.confirm.pause')
      }
      cancelLabel={t('emergency.cancel')}
      option={optionOf(closeOption, t)}
      requireOption={retryClose}
      busy={stopping}
      footer={
        <>
          {closeOption.kind === 'none' && (
            <Typography sx={{ fontSize: 14 }}>
              {t(retryClose ? 'emergency.nothingLeft' : 'emergency.noPositions')}
            </Typography>
          )}
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {t('emergency.manualNote')}
          </Typography>
          {failure !== null && <Alert severity="error">{t(failure.key, failure.values)}</Alert>}
        </>
      }
      onConfirm={({ optionChecked }) => {
        onConfirm(optionChecked);
      }}
      onCancel={onCancel}
    >
      {channelCount === null
        ? t('emergency.channelsUnknown')
        : t('emergency.channels', { count: channelCount })}
    </ConfirmSheet>
  );
}

type Translate = ReturnType<typeof useTranslation>['t'];

/** The close option of `ConfirmSheet` for each state of M4 R6 and R7; hidden when nothing to close. */
function optionOf(closeOption: CloseOption, t: Translate) {
  const label = t('emergency.closeOption.label');
  switch (closeOption.kind) {
    case 'none':
      return undefined;
    case 'loading':
      return { label, disabled: true, description: t('emergency.closeOption.loading') };
    case 'unavailable': {
      const reason = reasonMessage('POSITIONS_UNAVAILABLE');
      return { label, disabled: true, description: t(reason.key, reason.values) };
    }
    case 'available':
      return {
        label,
        description: t('emergency.closeOption.description'),
        details: <PositionList positions={closeOption.positions} />,
      };
  }
}

/** Each FXMate position with its current result in the account currency (R4, Q27). */
function PositionList({ positions }: { positions: readonly FxmatePosition[] }): ReactElement {
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {positions.map((position) => (
        <PositionLine key={position.id} position={position} />
      ))}
    </Box>
  );
}

/** E8's label for a position that is not simply open (M4 §4 "No current result"). */
const STATUS_NOTE = {
  OPENING: 'positions.status.OPENING',
  TRACKING_LOST: 'positions.status.TRACKING_LOST',
  CLOSING: 'positions.status.CLOSING',
} as const;

function PositionLine({ position }: { position: FxmatePosition }): ReactElement {
  const { t } = useTranslation();
  const note =
    position.status === 'OPENING' ||
    position.status === 'TRACKING_LOST' ||
    position.status === 'CLOSING'
      ? t(STATUS_NOTE[position.status])
      : null;
  return (
    <Box component="li" sx={{ py: 1, fontSize: 14, ...ROW_SEPARATOR_SX }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
        <PositionLabel side={position.side} symbol={position.symbol} lots={position.lots} />
        {hasCurrentResult(position) ? (
          <Amount {...position.floatingResult} signed />
        ) : (
          <Box component="span" sx={{ color: 'text.secondary', flex: 'none' }}>
            {t('emergency.resultUnknown')}
          </Box>
        )}
      </Box>
      {note !== null && <Box sx={{ fontSize: 13, color: 'text.secondary' }}>{note}</Box>}
    </Box>
  );
}
